// LoadingContext.js

import {LoadingComponent} from 'components/loading/Loading';
import {resolveRouteLoadingPage} from 'components/loading/routeLoadingPages';
import {useAppOverrides} from 'contexts/AppOverridesContext';
import {
  createContext,
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

/**
 * How long the overlay stays up after its last loader unmounts. A page often
 * hands off from one loader to the next (the course gate, then a lazy
 * route); within this grace the handoff reads as one continuous loading
 * screen instead of a flicker and a restarted animation.
 */
export const LOADING_HIDE_GRACE_MS = 250;

const defaultValue = {
  isLoading: false,
  showLoading: () => {},
  hideLoading: () => {},
};

export const LoadingContext = createContext(defaultValue);

/**
 * Mirrors the overlay's visibility outside React, so code running on a plain
 * timer can read it. Set together with the overlay's own state, so it never
 * disagrees with what the user sees (including the hide grace).
 */
let globalLoadingVisible = false;

/**
 * Whether the global loading overlay is currently covering the page.
 *
 * @return True while a mounted `<Loading/>` is asking for the overlay; false
 *   before the app has mounted anything.
 */
export function isGlobalLoadingVisible(): boolean {
  return globalLoadingVisible;
}

/** The overlay's state: whether it shows, and which showing this is. */
interface OverlayState {
  /** Whether the overlay covers the page. */
  isVisible: boolean;
  /**
   * Counts the times the overlay has appeared after being hidden. Keys the
   * overlay, so its animation restarts only for a new loading phase.
   */
  showing: number;
}

/**
 * Provides the global loading overlay. Each mounted `<Loading/>` asks for it
 * (`showLoading`) and gives it back on unmount (`hideLoading`); the overlay
 * shows while any loader asks and hides {@link LOADING_HIDE_GRACE_MS} after
 * the last one leaves, unless another arrives first.
 *
 * @param props.children - The app.
 * @return The provider with the overlay after its children.
 * @throws Never.
 */
export function LoadingProvider({children}) {
  const [overlay, setOverlay] = useState<OverlayState>({
    isVisible: false,
    showing: 0,
  });
  const askingRef = useRef(0);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const showLoading = useCallback(() => {
    askingRef.current += 1;
    clearTimeout(hideTimerRef.current);
    globalLoadingVisible = true;
    setOverlay(current =>
      current.isVisible
        ? current
        : {isVisible: true, showing: current.showing + 1},
    );
  }, []);

  const hideLoading = useCallback(() => {
    askingRef.current = Math.max(0, askingRef.current - 1);
    if (askingRef.current > 0) {
      return;
    }
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (askingRef.current > 0) {
        return;
      }
      globalLoadingVisible = false;
      setOverlay(current =>
        current.isVisible ? {...current, isVisible: false} : current,
      );
    }, LOADING_HIDE_GRACE_MS);
  }, []);

  const value = useMemo(
    () => ({isLoading: overlay.isVisible, showLoading, hideLoading}),
    [overlay.isVisible, showLoading, hideLoading],
  );

  return (
    <LoadingContext.Provider value={value}>
      {children}
      <GlobalLoadingOverlay
        isLoading={overlay.isVisible}
        showing={overlay.showing}
      />
    </LoadingContext.Provider>
  );
}

export function useLoadingContext() {
  return useContext(LoadingContext);
}

interface GlobalLoadingOverlayProps {
  /** Whether the overlay covers the page. */
  isLoading: boolean;
  /** Which showing this is; a new value restarts the animation. */
  showing: number;
}

const GlobalLoadingOverlay = memo(function GlobalLoadingOverlay({
  isLoading,
  showing,
}: GlobalLoadingOverlayProps) {
  const {LoadingPage} = useAppOverrides();
  // A route's own skeleton wins over the deploy-wide animation.
  const LoadingContent =
    resolveRouteLoadingPage(window.location.pathname) ?? LoadingPage ?? LoadingComponent;

  return (
    <LoadingOuter
      role="status"
      aria-live="polite"
      aria-hidden={!isLoading}
      style={{
        opacity: isLoading ? 1 : 0,
        pointerEvents: 'none',
      }}
      key={showing}
    >
      <VisuallyHidden>Loading, please wait...</VisuallyHidden>
      <LoadingContent />
    </LoadingOuter>
  );
});

const LoadingOuter = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  transition: opacity 0.3s ease;
`;

// Utility for visually-hidden text (SR-only style).
// You can move this to a shared location if you need it elsewhere.
const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`;
